import React, { useEffect, useMemo } from 'react';
import { Tabs, Badge } from 'antd';
import HealthBadge, { healthColor } from './HealthBadge';
import IssuesList from './IssuesList';
import ProyectoOperacionPanel from './ProyectoOperacionPanel';

const severityOrder = { critical: 0, warning: 1, unknown: 2, ok: 3 };

export default function AppPanel({ app, proyectos, activeProyectoId, onProyectoChange }) {
  const list = useMemo(() => {
    const rows = [...(proyectos || [])];
    rows.sort((a, b) => {
      const sa = severityOrder[a.estado] ?? 9;
      const sb = severityOrder[b.estado] ?? 9;
      if (sa !== sb) return sa - sb;
      return (a.orden_dashboard || 0) - (b.orden_dashboard || 0);
    });
    return rows;
  }, [proyectos]);

  const kpis = useMemo(() => {
    const counts = { ok: 0, warning: 0, critical: 0, unknown: 0 };
    let issues = 0;
    list.forEach((p) => {
      counts[p.estado] = (counts[p.estado] || 0) + 1;
      issues += p.contadores?.issues || 0;
    });
    return { ...counts, issues, total: list.length };
  }, [list]);

  const allIssues = useMemo(() => {
    const issues = [];
    list.forEach((p) => {
      (p.issues_top3 || []).forEach((iss) => {
        issues.push({ ...iss, entidad: `${p.nombre}: ${iss.entidad}` });
      });
    });
    issues.sort((a, b) => (a.severidad === 'critical' ? 0 : 1) - (b.severidad === 'critical' ? 0 : 1));
    return issues;
  }, [list]);

  const current = activeProyectoId && list.some((p) => String(p.id) === String(activeProyectoId))
    ? String(activeProyectoId)
    : (list[0] ? String(list[0].id) : undefined);

  useEffect(() => {
    if (current && String(activeProyectoId || '') !== current) {
      onProyectoChange?.(current);
    }
  }, [current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!list.length) {
    return <p style={{ color: '#8392a5' }}>Esta app no tiene proyectos visibles en el dashboard.</p>;
  }

  return (
    <div>
      <div className="yzen-kpi-grid" style={{ marginBottom: 16 }}>
        {[
          { label: 'Proyectos', value: kpis.total, color: '#0168fa' },
          { label: 'OK', value: kpis.ok, color: '#10b759' },
          { label: 'Atención', value: kpis.warning, color: '#fd7e14' },
          { label: 'Críticos', value: kpis.critical, color: '#dc3545' },
          { label: 'Issues', value: kpis.issues, color: '#8392a5' },
        ].map((k) => (
          <div key={k.label} className="yzen-kpi-mini">
            <div className="yzen-kpi-mini-value" style={{ color: k.color }}>{k.value}</div>
            <div className="yzen-kpi-mini-label">{k.label}</div>
          </div>
        ))}
      </div>

      {app?.descripcion ? (
        <p style={{ color: '#8392a5', fontSize: 13, marginTop: 0 }}>{app.descripcion}</p>
      ) : null}

      <div className="yzen-card" style={{ marginBottom: 16 }}>
        <h3 className="yzen-card-title">Issues de la app</h3>
        <IssuesList issues={allIssues} max={10} />
      </div>

      <Tabs
        type="card"
        size="small"
        activeKey={current}
        onChange={(key) => onProyectoChange?.(key)}
        items={list.map((p) => ({
          key: String(p.id),
          label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: healthColor(p.estado) }} />
              {p.nombre}
              {(p.contadores?.issues || 0) > 0 ? <Badge count={p.contadores.issues} size="small" /> : null}
            </span>
          ),
          children: current === String(p.id) ? (
            <ProyectoOperacionPanel proyectoId={p.id} />
          ) : null,
        }))}
      />
    </div>
  );
}

export { HealthBadge };
